/**
 * Hand-crafted line-style SVG glyphs for the marketplace home tiles.
 *
 * Why not MUI icons:
 *   The default Material set is intentionally bold and filled — at
 *   small tile sizes that reads as a "coloring book" sticker rather
 *   than a professional product mark. These glyphs are uniformly
 *   24x24, single-stroke (1.5px), uncolored (``stroke="currentColor"``)
 *   so a single ``color`` prop or ``sx`` value styles them in line
 *   with the surrounding chrome.
 *
 * One named export per category slug from ``categories_seed.py``.
 * The component contract is intentionally minimal: ``size`` (defaults
 * to 24) sets width/height, every glyph inherits ``currentColor`` so
 * the parent controls color.
 */

import React from 'react';

// Common stroke props so every glyph reads consistently.
const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const Svg = ({ size = 24, children }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

// ---- per-category glyphs --------------------------------------------------

export const FinanceGlyph = (p) => (
  <Svg {...p}>
    {/* Candlestick chart: axis + three OHLC marks */}
    <path d="M3 4v17h18" {...STROKE} />
    <path d="M7 17v-4M7 13l0-3" {...STROKE} />
    <rect x="5.5" y="11" width="3" height="5" rx="0.6" {...STROKE} />
    <path d="M12 9v8M12 14v-7" {...STROKE} />
    <rect x="10.5" y="8" width="3" height="4" rx="0.6" {...STROKE} />
    <path d="M17 6v10M17 14v-3" {...STROKE} />
    <rect x="15.5" y="11" width="3" height="5" rx="0.6" {...STROKE} />
  </Svg>
);

export const CryptoGlyph = (p) => (
  <Svg {...p}>
    {/* Coin with B-shape inside */}
    <circle cx="12" cy="12" r="8.5" {...STROKE} />
    <path d="M9.5 7.5v9M9.5 7.5h3.5a2.25 2.25 0 1 1 0 4.5H9.5M9.5 12h4a2.25 2.25 0 1 1 0 4.5H9.5"
          {...STROKE} />
    <path d="M11 6v1.5M11 16.5V18M13 6v1.5M13 16.5V18" {...STROKE} />
  </Svg>
);

export const MacroGlyph = (p) => (
  <Svg {...p}>
    {/* Greek-temple style government / bank building */}
    <path d="M3 9l9-5 9 5" {...STROKE} />
    <path d="M3 9h18" {...STROKE} />
    <path d="M5 11v8M9 11v8M15 11v8M19 11v8" {...STROKE} />
    <path d="M3 19h18" {...STROKE} />
    <path d="M3 21h18" {...STROKE} />
  </Svg>
);

export const EnergyGlyph = (p) => (
  <Svg {...p}>
    {/* Outlined lightning bolt */}
    <path d="M13.5 3 5 13.5h6L9 21l9-11.5h-6L13.5 3z" {...STROKE} />
  </Svg>
);

export const WeatherGlyph = (p) => (
  <Svg {...p}>
    {/* Sun behind a cloud */}
    <circle cx="17" cy="7" r="2.5" {...STROKE} />
    <path d="M17 2.5V4M17 10v1.5M21.5 7H20M14 7h-1.5M20.18 3.82l-1.06 1.06M14.88 9.12l-1.06 1.06"
          {...STROKE} />
    <path d="M6.5 19a3.5 3.5 0 0 1-.5-6.97A5 5 0 0 1 15 13a3.5 3.5 0 0 1-.5 6H6.5z"
          {...STROKE} />
  </Svg>
);

export const ClimateGlyph = (p) => (
  <Svg {...p}>
    {/* Leaf with vein */}
    <path d="M20.5 3.5C9 3.5 3.5 9 3.5 20.5c11.5 0 17-5.5 17-17z" {...STROKE} />
    <path d="M3.5 20.5C8 16 13 12 20.5 8" {...STROKE} />
  </Svg>
);

export const GeoGlyph = (p) => (
  <Svg {...p}>
    {/* Mountain peaks with sun */}
    <circle cx="17" cy="6" r="1.8" {...STROKE} />
    <path d="M3 20 9 11l4 5 2-2.5 6 6.5z" {...STROKE} />
  </Svg>
);

export const HealthGlyph = (p) => (
  <Svg {...p}>
    {/* Heart with EKG pulse line */}
    <path d="M3 11h4l2-3 3 6 2-3h7" {...STROKE} />
    <path d="M20 6.5a4.5 4.5 0 0 0-8-2.8 4.5 4.5 0 0 0-7.4 4.5"
          {...STROKE} />
    <path d="M4.6 8.2 12 17l7.4-8.8" {...STROKE} />
  </Svg>
);

export const RoboticsGlyph = (p) => (
  <Svg {...p}>
    {/* Robot head: rounded rect, antenna, eyes */}
    <path d="M12 3v2" {...STROKE} />
    <circle cx="12" cy="2.6" r="0.9" {...STROKE} />
    <rect x="4" y="6" width="16" height="13" rx="2.5" {...STROKE} />
    <circle cx="9" cy="12.5" r="1.2" {...STROKE} />
    <circle cx="15" cy="12.5" r="1.2" {...STROKE} />
    <path d="M9.5 16h5" {...STROKE} />
    <path d="M2 12v3M22 12v3" {...STROKE} />
  </Svg>
);

export const IoTGlyph = (p) => (
  <Svg {...p}>
    {/* Wifi-style concentric arcs around a node */}
    <path d="M5 12.5a8.5 8.5 0 0 1 14 0" {...STROKE} />
    <path d="M7.5 15a5 5 0 0 1 9 0" {...STROKE} />
    <circle cx="12" cy="18" r="1.4" {...STROKE} />
    <path d="M2 9.5a13 13 0 0 1 20 0" {...STROKE} />
  </Svg>
);

export const TransportGlyph = (p) => (
  <Svg {...p}>
    {/* Side-view minimalist car silhouette */}
    <path d="M3 14h1l1.6-4.5A2 2 0 0 1 7.5 8h9a2 2 0 0 1 1.9 1.5L20 14h1v3h-2"
          {...STROKE} />
    <path d="M5 17H3v-3" {...STROKE} />
    <path d="M14 17H10" {...STROKE} />
    <circle cx="7.5" cy="17" r="1.8" {...STROKE} />
    <circle cx="16.5" cy="17" r="1.8" {...STROKE} />
  </Svg>
);

export const SportsGlyph = (p) => (
  <Svg {...p}>
    {/* Ball with seam */}
    <circle cx="12" cy="12" r="8.5" {...STROKE} />
    <path d="M3.5 12c4 0 6 2 8.5 2s4.5-2 8.5-2" {...STROKE} />
    <path d="M6 6.5C9 9 9.5 11 12 11s3-2 6-4.5" {...STROKE} />
    <path d="M6 17.5C9 15 9.5 13 12 13s3 2 6 4.5" {...STROKE} />
  </Svg>
);

export const WebGlyph = (p) => (
  <Svg {...p}>
    {/* Globe with longitude/latitude lines */}
    <circle cx="12" cy="12" r="8.5" {...STROKE} />
    <path d="M3.5 12h17" {...STROKE} />
    <path d="M12 3.5c2.5 3 4 6 4 8.5s-1.5 5.5-4 8.5" {...STROKE} />
    <path d="M12 3.5c-2.5 3-4 6-4 8.5s1.5 5.5 4 8.5" {...STROKE} />
  </Svg>
);

export const GovGlyph = (p) => (
  <Svg {...p}>
    {/* Capitol-style courthouse building */}
    <path d="M12 3 3 8h18z" {...STROKE} />
    <path d="M5 8v10M9 8v10M15 8v10M19 8v10" {...STROKE} />
    <path d="M3 18h18M3 21h18" {...STROKE} />
    <circle cx="12" cy="6.2" r="0.6" {...STROKE} />
  </Svg>
);

export const OtherGlyph = (p) => (
  <Svg {...p}>
    {/* 3x3 dot grid - generic "everything else" */}
    {[6, 12, 18].flatMap((y) => (
      [6, 12, 18].map((x) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="1.4" {...STROKE} />
      ))
    ))}
  </Svg>
);

export const CATEGORY_GLYPHS = {
  finance:   FinanceGlyph,
  crypto:    CryptoGlyph,
  macro:     MacroGlyph,
  energy:    EnergyGlyph,
  weather:   WeatherGlyph,
  climate:   ClimateGlyph,
  geo:       GeoGlyph,
  health:    HealthGlyph,
  robotics:  RoboticsGlyph,
  iot:       IoTGlyph,
  transport: TransportGlyph,
  sports:    SportsGlyph,
  web:       WebGlyph,
  gov:       GovGlyph,
  other:     OtherGlyph,
};

// Single source of truth for the per-category accent color. Imported
// by both the marketplace home (border + hover tint) and the listing
// detail page (icon ring + chip color). Keep in sync with the BE
// taxonomy (categories_seed.py).
export const CATEGORY_ACCENTS = {
  finance:   '#0EA5A4',
  crypto:    '#F59E0B',
  macro:     '#6366F1',
  energy:    '#FB923C',
  weather:   '#0EA5E9',
  climate:   '#22C55E',
  geo:       '#A855F7',
  health:    '#F43F5E',
  robotics:  '#475569',
  iot:       '#06B6D4',
  transport: '#3B82F6',
  sports:    '#EC4899',
  web:       '#84CC16',
  gov:       '#64748B',
  other:     '#A78BFA',
};

/**
 * Resolve the accent color + glyph for a given category. Accepts
 * either a top-level slug ("finance") or a dotted child slug
 * ("finance.equities") - we walk up to the root before lookup so
 * children inherit their parent's brand color.
 */
export const accentForCategory = (slug, fallback = '#6B7280') => {
  if (!slug) return fallback;
  const root = String(slug).split('.')[0];
  return CATEGORY_ACCENTS[root] || fallback;
};

export const glyphForCategory = (slug) => {
  if (!slug) return OtherGlyph;
  const root = String(slug).split('.')[0];
  return CATEGORY_GLYPHS[root] || OtherGlyph;
};
